import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { setEventCommentsEnabled, setEventRsvpSeriesResponsesEnabled } from '../../../../utils/boardApi';
import '../../../../styles/events.css';
import './eventSetupForm.css';
function cleanText(value) {
  return typeof value === 'string' ? value.trim() : '';
}
function normalizeDefaultSlots(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.max(1, Math.floor(n));
}
export default function EventSetupForm({
  isOpen,
  teamId,
  teamTags = [],
  userId = null,
  onClose,
  onSave
}) {
  const [locations, setLocations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [eventCommentsEnabled, setEventCommentsEnabledState] = useState(false);
  const [initialEventCommentsEnabled, setInitialEventCommentsEnabled] = useState(false);
  const [rsvpSeriesResponsesEnabled, setRsvpSeriesResponsesEnabled] = useState(true);
  const [initialRsvpSeriesResponsesEnabled, setInitialRsvpSeriesResponsesEnabled] = useState(true);
  const [locationsExpanded, setLocationsExpanded] = useState(false);
  const [rolesExpanded, setRolesExpanded] = useState(false);
  const tagOptions = useMemo(() => Array.isArray(teamTags) ? teamTags.filter(t => t?.name).map(t => t.name) : [], [teamTags]);
  useEffect(() => {
    if (!isOpen || !teamId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [teamResult, locResult, roleResult] = await Promise.allSettled([getDoc(doc(db, 'teams', teamId)), getDocs(collection(db, 'teams', teamId, 'locations')), getDocs(collection(db, 'teams', teamId, 'eventRoles'))]);
        if (cancelled) return;
        if (teamResult.status === 'fulfilled' && teamResult.value.exists()) {
          const eventSettings = teamResult.value.data()?.eventSettings || {};
          const commentsEnabled = Boolean(eventSettings.commentsEnabled);
          const seriesResponsesEnabled = eventSettings.rsvpSeriesResponsesEnabled !== false;
          setEventCommentsEnabledState(commentsEnabled);
          setInitialEventCommentsEnabled(commentsEnabled);
          setRsvpSeriesResponsesEnabled(seriesResponsesEnabled);
          setInitialRsvpSeriesResponsesEnabled(seriesResponsesEnabled);
        } else {
          setEventCommentsEnabledState(false);
          setInitialEventCommentsEnabled(false);
          setRsvpSeriesResponsesEnabled(true);
          setInitialRsvpSeriesResponsesEnabled(true);
        }
        if (locResult.status === 'fulfilled') {
          setLocations(locResult.value.docs.map(d => ({
            id: d.id,
            ...d.data(),
            _deleted: false
          })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        } else {
          const code = locResult.reason?.code || '';
          if (code !== 'permission-denied') {}
          setLocations([]);
        }
        if (roleResult.status === 'fulfilled') {
          setRoles(roleResult.value.docs.map(d => ({
            id: d.id,
            ...d.data(),
            defaultSlots: normalizeDefaultSlots(d.data()?.defaultSlots),
            allowedTags: Array.isArray(d.data()?.allowedTags) ? d.data().allowedTags : [],
            requiresApproval: d.data()?.requiresApproval !== false,
            active: d.data()?.active !== false,
            _deleted: false
          })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        } else {
          const code = roleResult.reason?.code || '';
          if (code !== 'permission-denied') {}
          setRoles([]);
        }
        const permissionDenied = (teamResult.status === 'rejected' && teamResult.reason?.code === 'permission-denied') || (locResult.status === 'rejected' && locResult.reason?.code === 'permission-denied') || (roleResult.status === 'rejected' && roleResult.reason?.code === 'permission-denied');
        if (permissionDenied) {
          setError('Permission denied loading locations/roles. Check Firestore rules deployment and admin membership.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);
  const patchLocation = (index, patch) => {
    setLocations(prev => prev.map((item, i) => i === index ? {
      ...item,
      ...patch
    } : item));
  };
  const patchRole = (index, patch) => {
    setRoles(prev => prev.map((item, i) => i === index ? {
      ...item,
      ...patch
    } : item));
  };
  const toggleRoleTag = (index, tagName) => {
    setRoles(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const next = new Set(Array.isArray(item.allowedTags) ? item.allowedTags : []);
      if (next.has(tagName)) next.delete(tagName);else next.add(tagName);
      return {
        ...item,
        allowedTags: Array.from(next)
      };
    }));
  };
  const markLocationDeleted = index => {
    const location = locations[index];
    if (!window.confirm(`Remove "${location?.name || 'this saved location'}"? It will be deleted when you save event setup.`)) return;
    setLocations(prev => prev.map((item, i) => i === index ? {
      ...item,
      _deleted: true
    } : item));
  };
  const markRoleDeleted = index => {
    const role = roles[index];
    if (!window.confirm(`Remove "${role?.name || 'this role preset'}"? It will be deleted when you save event setup.`)) return;
    setRoles(prev => prev.map((item, i) => i === index ? {
      ...item,
      _deleted: true
    } : item));
  };
  const addLocation = () => {
    setLocations(prev => [...prev, {
      id: null,
      name: '',
      street: '',
      city: '',
      postcode: '',
      country: '',
      _deleted: false
    }]);
  };
  const addRole = () => {
    setRoles(prev => [...prev, {
      id: null,
      name: '',
      defaultSlots: 1,
      allowedTags: [],
      requiresApproval: true,
      active: true,
      _deleted: false
    }]);
  };
  const handleSubmit = async e => {
    e.preventDefault();
    if (!teamId) return;
    setSaving(true);
    setError('');
    try {
      const locationCreates = [];
      const locationUpdates = [];
      const locationDeletes = [];
      for (const loc of locations) {
        if (loc._deleted) {
          if (loc.id) locationDeletes.push(loc.id);
          continue;
        }
        const payload = {
          name: cleanText(loc.name),
          street: cleanText(loc.street),
          city: cleanText(loc.city),
          postcode: cleanText(loc.postcode),
          country: cleanText(loc.country),
          updatedAt: serverTimestamp()
        };
        if (!payload.name || !payload.street || !payload.postcode || !payload.country) {
          throw new Error('Each saved location needs name, street, postcode, and country.');
        }
        if (loc.id) {
          locationUpdates.push({
            id: loc.id,
            payload
          });
        } else {
          locationCreates.push({
            ...payload,
            createdAt: serverTimestamp()
          });
        }
      }
      const roleCreates = [];
      const roleUpdates = [];
      const roleDeletes = [];
      for (const role of roles) {
        if (role._deleted) {
          if (role.id) roleDeletes.push(role.id);
          continue;
        }
        const payload = {
          name: cleanText(role.name),
          defaultSlots: normalizeDefaultSlots(role.defaultSlots),
          allowedTags: Array.isArray(role.allowedTags) ? role.allowedTags.filter(t => typeof t === 'string' && t.trim() !== '') : [],
          requiresApproval: role.requiresApproval !== false,
          active: role.active !== false,
          updatedAt: serverTimestamp(),
          updatedBy: userId
        };
        if (!payload.name) {
          throw new Error('Each event role preset needs a name.');
        }
        if (role.id) {
          roleUpdates.push({
            id: role.id,
            payload
          });
        } else {
          roleCreates.push({
            ...payload,
            createdAt: serverTimestamp(),
            createdBy: userId
          });
        }
      }
      for (const id of locationDeletes) {
        await deleteDoc(doc(db, 'teams', teamId, 'locations', id));
      }
      for (const item of locationUpdates) {
        await updateDoc(doc(db, 'teams', teamId, 'locations', item.id), item.payload);
      }
      for (const payload of locationCreates) {
        await addDoc(collection(db, 'teams', teamId, 'locations'), payload);
      }
      for (const id of roleDeletes) {
        await deleteDoc(doc(db, 'teams', teamId, 'eventRoles', id));
      }
      for (const item of roleUpdates) {
        await updateDoc(doc(db, 'teams', teamId, 'eventRoles', item.id), item.payload);
      }
      for (const payload of roleCreates) {
        await addDoc(collection(db, 'teams', teamId, 'eventRoles'), payload);
      }
      if (eventCommentsEnabled !== initialEventCommentsEnabled) {
        await setEventCommentsEnabled({
          teamId,
          enabled: eventCommentsEnabled
        });
        setInitialEventCommentsEnabled(eventCommentsEnabled);
      }
      if (rsvpSeriesResponsesEnabled !== initialRsvpSeriesResponsesEnabled) {
        await setEventRsvpSeriesResponsesEnabled({
          teamId,
          enabled: rsvpSeriesResponsesEnabled
        });
        setInitialRsvpSeriesResponsesEnabled(rsvpSeriesResponsesEnabled);
      }
      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save event setup.');
    } finally {
      setSaving(false);
    }
  };
  if (!isOpen) return null;
  const visibleLocations = locations.map((loc, index) => ({
    loc,
    index
  })).filter(entry => !entry.loc._deleted);
  const visibleRoles = roles.map((role, index) => ({
    role,
    index
  })).filter(entry => !entry.role._deleted);
  return <form onSubmit={handleSubmit} className="modal-form event-setup-form">
      <h2 className="event-setup-title">Event Setup</h2>
      <p className="event-setup-subtitle">Manage reusable saved locations, role presets, and event features.</p>

      {error && <p className="modal-error">{error}</p>}

      <section className="form-section event-setup-section">
        <div className="event-setup-section-head">
          <div className="event-setup-disclosure" style={{
        cursor: 'default'
      }}>
            <span className="event-setup-disclosure-title">
              <span>Event Comments</span>
            </span>
            <span className="event-setup-disclosure-meta">
              {eventCommentsEnabled ? 'On' : 'Off'}
            </span>
          </div>
        </div>
        <div className="checkbox-row event-setup-checkbox-row" style={{ marginTop: 12 }}>
          <input type="checkbox" id="event-comments-enabled" checked={eventCommentsEnabled} onChange={e => setEventCommentsEnabledState(e.target.checked)} />
          <label htmlFor="event-comments-enabled">
            Allow members to comment on events they can access
          </label>
        </div>
        <p className="text-muted event-setup-help">
          Comments appear at the bottom of event details and use message board moderation.
        </p>
      </section>

      <section className="form-section event-setup-section">
        <div className="event-setup-section-head">
          <div className="event-setup-disclosure" style={{
        cursor: 'default'
      }}>
            <span className="event-setup-disclosure-title">
              <span>RSVP Series Responses</span>
            </span>
            <span className="event-setup-disclosure-meta">
              {rsvpSeriesResponsesEnabled ? 'On' : 'Off'}
            </span>
          </div>
        </div>
        <div className="checkbox-row event-setup-checkbox-row" style={{ marginTop: 12 }}>
          <input type="checkbox" id="rsvp-series-responses-enabled" checked={rsvpSeriesResponsesEnabled} onChange={e => setRsvpSeriesResponsesEnabled(e.target.checked)} />
          <label htmlFor="rsvp-series-responses-enabled">
            Allow series RSVP responses
          </label>
        </div>
        <p className="text-muted event-setup-help">
          Turn off to require a separate RSVP for each date in recurring events.
        </p>
      </section>

      <section className="form-section event-setup-section">
        <div className="event-setup-section-head">
          <button type="button" className="event-setup-disclosure" aria-expanded={locationsExpanded} onClick={() => setLocationsExpanded(open => !open)}>
            <span className="event-setup-disclosure-title">
              <span aria-hidden="true">{locationsExpanded ? '▾' : '▸'}</span>
              <span>Saved Locations</span>
            </span>
            <span className="event-setup-disclosure-meta">
              {visibleLocations.length} saved
            </span>
          </button>
        </div>
        {locationsExpanded && <>
            {loading ? <p className="text-muted">Loading locations…</p> : visibleLocations.length === 0 ? <p className="text-muted">No saved locations.</p> : <div className="event-setup-list">
                {visibleLocations.map(({
            loc,
            index
          }) => <div key={loc.id || `new-loc-${index}`} className="event-setup-item">
                    <div className="event-setup-grid event-setup-grid--locations">
                      <label className="event-setup-field">
                        <span className="field-label">Name</span>
                        <input className="input event-setup-input" value={loc.name || ''} onChange={e => patchLocation(index, {
                  name: e.target.value
                })} />
                      </label>
                      <label className="event-setup-field">
                        <span className="field-label">Street</span>
                        <input className="input event-setup-input" value={loc.street || ''} onChange={e => patchLocation(index, {
                  street: e.target.value
                })} />
                      </label>
                      <label className="event-setup-field">
                        <span className="field-label">City</span>
                        <input className="input event-setup-input" value={loc.city || ''} onChange={e => patchLocation(index, {
                  city: e.target.value
                })} />
                      </label>
                      <label className="event-setup-field">
                        <span className="field-label">Postcode</span>
                        <input className="input event-setup-input" value={loc.postcode || ''} onChange={e => patchLocation(index, {
                  postcode: e.target.value
                })} />
                      </label>
                      <label className="event-setup-field">
                        <span className="field-label">Country</span>
                        <input className="input event-setup-input" value={loc.country || ''} onChange={e => patchLocation(index, {
                  country: e.target.value
                })} />
                      </label>
                    </div>
                    <div className="event-setup-item-actions">
                      <button type="button" className="button button-ghost button--xs" onClick={() => markLocationDeleted(index)}>
                        Remove location
                      </button>
                    </div>
                  </div>)}
              </div>}
            <div className="event-setup-inline-actions">
              <button type="button" className="button button-primary button--xs" onClick={addLocation} disabled={loading}>
                + Add location
              </button>
            </div>
          </>}
      </section>

      <section className="form-section event-setup-section">
        <div className="event-setup-section-head">
          <button type="button" className="event-setup-disclosure" aria-expanded={rolesExpanded} onClick={() => setRolesExpanded(open => !open)}>
            <span className="event-setup-disclosure-title">
              <span aria-hidden="true">{rolesExpanded ? '▾' : '▸'}</span>
              <span>Event Role Presets</span>
            </span>
            <span className="event-setup-disclosure-meta">
              {visibleRoles.length} preset{visibleRoles.length === 1 ? '' : 's'}
            </span>
          </button>
        </div>
        {rolesExpanded && <>
            {loading ? <p className="text-muted">Loading role presets…</p> : visibleRoles.length === 0 ? <p className="text-muted">No role presets.</p> : <div className="event-setup-list">
                {visibleRoles.map(({
            role,
            index
          }) => <div key={role.id || `new-role-${index}`} className="event-setup-item">
                    <div className="event-setup-grid event-setup-grid--roles">
                      <label className="event-setup-field">
                        <span className="field-label">Role name</span>
                        <input className="input event-setup-input" value={role.name || ''} onChange={e => patchRole(index, {
                  name: e.target.value
                })} />
                      </label>
                      <label className="event-setup-field">
                        <span className="field-label">Default slots</span>
                        <input className="input event-setup-input" type="number" min="1" value={normalizeDefaultSlots(role.defaultSlots)} onChange={e => patchRole(index, {
                  defaultSlots: e.target.value
                })} />
                      </label>
                    </div>

                    <div className="checkbox-row event-setup-checkbox-row">
                      <input type="checkbox" id={`preset-active-${role.id || index}`} checked={role.active !== false} onChange={e => patchRole(index, {
                active: e.target.checked
              })} />
                      <label htmlFor={`preset-active-${role.id || index}`}>
                        Preset is active
                      </label>
                    </div>
                    <p className="text-muted event-setup-help">
                      Inactive presets are hidden from the New Event preset picker.
                    </p>

                    <div className="checkbox-row event-setup-checkbox-row">
                      <input type="checkbox" id={`preset-approval-${role.id || index}`} checked={role.requiresApproval !== false} onChange={e => patchRole(index, {
                requiresApproval: e.target.checked
              })} />
                      <label htmlFor={`preset-approval-${role.id || index}`}>
                        Require admin approval by default
                      </label>
                    </div>

                    <div className="event-setup-tags-block">
                      <div className="text-muted event-setup-tags-title">Allowed tags (optional)</div>
                      {tagOptions.length === 0 ? <p className="text-muted">No team tags available.</p> : <div className="event-setup-tags-grid">
                          {tagOptions.map(tagName => <label key={`${role.id || index}:${tagName}`} className="event-setup-tag-option">
                              <input type="checkbox" checked={Array.isArray(role.allowedTags) && role.allowedTags.includes(tagName)} onChange={() => toggleRoleTag(index, tagName)} />
                              {tagName}
                            </label>)}
                        </div>}
                    </div>

                    <div className="event-setup-item-actions">
                      <button type="button" className="button button-ghost button--xs" onClick={() => markRoleDeleted(index)}>
                        Remove role preset
                      </button>
                    </div>
                  </div>)}
              </div>}
            <div className="event-setup-inline-actions">
              <button type="button" className="button button-primary button--xs" onClick={addRole} disabled={loading}>
                + Add role preset
              </button>
            </div>
          </>}
      </section>

      <div className="form-actions event-setup-footer">
        <button type="button" className="button button-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="button button-primary" disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save setup'}
        </button>
      </div>
    </form>;
}
